import {
  attr,
  computedOf,
  Ensure,
  html,
  on,
  prop,
  Signal,
  TNode,
  When,
} from '@tempots/dom'
import { PdfService, TrialProcessOptions } from '@/services/pdf-service'
import { DEFAULT_DESIGN } from '@rh/shared/templates/default-design'
import type { SavedDesign } from '@rh/shared/schemas/design'
import { TrialDesignPreviewPages } from './trial-design-preview-pages'
import { Button, ButtonLink } from '@tempots/beatui'
import { Track } from '@/services/track'
import { ProgressPanel, type ProgressPanelData } from './progress-panel'
import {
  buildTrialDesign,
  buildTrialTokenData,
} from '@rh/shared/templates/trial-design'
import type { ResumeState } from '@/models/resume-state'
import type { ContentBoundsReport } from '@rh/shared/utils/content-trim'

export type OnboardingValues = TrialProcessOptions

export type ResumeLivePreviewOptions = {
  notAsked?: () => TNode
  resume: Signal<ArrayBuffer | null>
  /** Server-measured margins driving the preview's trim (see TrialDesignPreviewPages). */
  contentBounds?: Signal<ContentBoundsReport | null>
  resumeState?: Signal<ResumeState>
  values: Signal<OnboardingValues>
  onDownload?: (options: {
    resume: ArrayBuffer
    values: OnboardingValues
    setDownloading: (value: boolean) => void
    setDownloaded: (value: boolean) => void
  }) => void
}

/**
 * Trial-page live preview: maps the trial form onto a SavedDesign (the same
 * document/rendering pipeline the logged-in Customize editor and the server
 * use) and previews it over the uploaded resume.
 */
export function ResumeLivePreview({
  notAsked,
  resume,
  contentBounds,
  resumeState,
  values,
  onDownload,
}: ResumeLivePreviewOptions) {
  const writeup = values.$.writeup.map(v =>
    v?.trim() != '' ? (v ?? null) : null
  )
  // The base design mirrors what the server's trial render uses: the
  // admin-uploaded default when one exists (fetched once, with its image
  // data URLs), else the compiled DEFAULT_DESIGN — which is also the
  // server's fallback, so preview and download stay in agreement.
  const baseDesign = prop<{
    design: SavedDesign
    images: Record<string, string>
  }>({ design: DEFAULT_DESIGN, images: {} })
  void PdfService.fetchTrialDefaultDesign().then(result => {
    if (result) baseDesign.set({ design: result.design, images: result.images })
  })
  const design = computedOf(
    values,
    baseDesign
  )((v, base) => buildTrialDesign(v, base.design))
  const images = baseDesign.map(b => b.images)
  const tokenData = values.map(buildTrialTokenData)
  const loading = prop(false)
  // Once the user has downloaded at least once, the footer's primary action
  // switches from downloading to signing up. Uploading a new resume resets it.
  const downloaded = prop(false)
  resume.on(() => downloaded.set(false))

  // Derive loading message from resumeState
  const loadingMessage = resumeState?.map(state => {
    if (state.type === 'uploading') {
      return 'Uploading file...'
    }
    if (state.type === 'converting') {
      return 'Converting to PDF...'
    }
    return null
  })

  // Create a signal for ProgressPanel data that updates reactively
  const progressPanelData = loadingMessage?.map(
    (msg): ProgressPanelData => ({
      title: msg ?? '',
    })
  )

  return Ensure(
    resume,
    resumeValue => {
      return html.div(
        attr.class('flex flex-col h-full space-y-4 overflow-hidden'),
        TrialDesignPreviewPages({
          design,
          tokenData,
          images,
          writeup,
          resume: resumeValue,
          contentBounds,
        }),
        onDownload != null
          ? html.div(
              When(
                downloaded,
                () =>
                  html.div(
                    attr.class(
                      'flex flex-col items-center gap-1 p-3 bg-teal-50 border-t border-teal-200'
                    ),
                    html.div(
                      attr.class('text-sm text-teal-900'),
                      '✓ Downloaded! Sign up to save this setup and brand more resumes.'
                    ),
                    ButtonLink(
                      { href: '/signup', color: 'primary', size: 'lg' },
                      attr.class('w-full'),
                      on.click(() =>
                        Track.click({
                          section: 'main',
                          element_type: 'button',
                          click_action: 'navigate',
                          element_id: 'signup_post_download',
                        })
                      ),
                      'Sign up and save your information'
                    ),
                    html.button(
                      attr.type('button'),
                      attr.class(
                        'text-sm text-teal-700 underline hover:text-teal-900 disabled:opacity-50'
                      ),
                      attr.disabled(loading),
                      on.click(() => {
                        Track.click({
                          section: 'main',
                          element_type: 'button',
                          click_action: 'submit',
                          element_id: 'download_again',
                        })
                        onDownload?.({
                          resume: resumeValue.value,
                          values: values.value,
                          setDownloading: v => loading.set(v),
                          setDownloaded: v => downloaded.set(v),
                        })
                      }),
                      'Download again'
                    )
                  ),
                () =>
                  Button(
                    {
                      loading,
                      fullWidth: true,
                      roundedness: 'none',
                      size: 'lg',
                      variant: 'outline',
                      color: 'primary',
                      onClick: () => {
                        onDownload?.({
                          resume: resumeValue.value,
                          values: values.value,
                          setDownloading: v => loading.set(v),
                          setDownloaded: v => downloaded.set(v),
                        })
                      },
                    },
                    'Download PDF'
                  )
              )
            )
          : null
      )
    },
    // Show loading panel during upload/conversion, otherwise show notAsked
    () => {
      if (loadingMessage != null && progressPanelData != null) {
        return Ensure(
          loadingMessage,
          () => ProgressPanel({ value: progressPanelData }),
          () => notAsked?.() ?? null
        )
      }
      return notAsked?.() ?? null
    }
  )
}
